import { useMemo, useState, useSyncExternalStore } from 'react';

type PersonalChecksBannerDismissedState = {
  isDismissed: boolean;
  dismiss: () => void;
};

export const PERSONAL_CHECKS_BANNER_DISMISSED_KEY =
  'personalChecksBannerDismissed';

const subscribeToStorage = (callback: () => void) => {
  const handler = (event: StorageEvent) => {
    if (event.key === PERSONAL_CHECKS_BANNER_DISMISSED_KEY) {
      callback();
    }
  };
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('storage', handler);
  };
};

const getSnapshot = () => {
  try {
    return (
      window.localStorage.getItem(PERSONAL_CHECKS_BANNER_DISMISSED_KEY) ===
      'true'
    );
  } catch {
    return undefined;
  }
};

const getServerSnapshot = () => {
  return undefined;
};

export const usePersonalChecksBannerDismissed = () => {
  const [isDismissedState, setIsDismissedState] = useState(false);
  const isDismissedInStorage = useSyncExternalStore(
    subscribeToStorage,
    getSnapshot,
    getServerSnapshot,
  );

  // If store isn't available, `isDismissedInStorage` will be `undefined`.
  // In this case, we use the state value to determine if the banner should be
  // dismissed.
  const isDismissed =
    typeof isDismissedInStorage === 'boolean'
      ? isDismissedInStorage
      : isDismissedState;

  return useMemo<PersonalChecksBannerDismissedState>(
    () => ({
      isDismissed,
      dismiss: () => {
        try {
          window.localStorage.setItem(
            PERSONAL_CHECKS_BANNER_DISMISSED_KEY,
            'true',
          );
          window.dispatchEvent(
            new StorageEvent('storage', {
              key: PERSONAL_CHECKS_BANNER_DISMISSED_KEY,
            }),
          );
        } catch {
          // Ignore storage failures (e.g. private mode); still hide for this session.
        }
        setIsDismissedState(true);
      },
    }),
    [isDismissed],
  );
};
